import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

/**
 * Renders the per-file column-mapper UI. The parent owns the mapping state
 * (one mapping per file), but each file's sniff payload tells us which
 * columns are available and what sample values they contain.
 *
 * Props:
 *  - sniff: SniffResult — { filename, year, month, available_fields,
 *    available_columns, sheets[], parse_errors }
 *  - mapping: Record<fieldKey, columnLetter> — current user-edited mapping
 *  - onMappingChange(fieldKey, letter): callback when a dropdown changes
 *
 * The mapping defaults to the first sheet's suggested_mapping; if the user
 * leaves a field unmapped we treat that field as "don't parse" rather than
 * an error so older sheets missing some columns still import.
 */
export default function ColumnMapper({ sniff, mapping, onMappingChange }) {
  if (!sniff || !sniff.sheets || sniff.sheets.length === 0) {
    return null;
  }

  // Take the first sheet's sample for the value previews. All sheets in
  // a workbook share a layout in practice (months × the same template).
  const sheet = sniff.sheets[0];
  const sampleByCol = buildSampleByCol(sheet.sample_rows);
  const headerByCol = buildHeaderByCol(sheet.header_cells);

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {sniff.filename}
          </Typography>
          <Chip
            size="small"
            label={`${sheet.source === 'headers' ? 'Headers detected' : sheet.source === 'shifted' ? 'Legacy column-A layout' : 'Default mapping'}`}
            color={
              sheet.source === 'headers'
                ? 'success'
                : sheet.source === 'shifted'
                  ? 'warning'
                  : 'default'
            }
            variant="outlined"
          />
          <Chip
            size="small"
            label={`${sniff.sheets.length} sheet${sniff.sheets.length === 1 ? '' : 's'}`}
            variant="outlined"
          />
          <Chip
            size="small"
            label={`Data starts at row ${sheet.data_start_row}`}
            variant="outlined"
          />
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Review the auto-detected column mapping. Hover a dropdown to see
          which Excel column it points at and the first few values from
          that column. Leave a field blank to skip it during import.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={1.5}>
          {sniff.available_fields.map((field) => {
            const currentLetter = mapping[field.key] || '';
            const sample = sampleByCol[currentLetter] || [];
            return (
              <Box
                key={field.key}
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ flex: { sm: '0 0 200px' }, fontWeight: 500 }}
                >
                  {field.label}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select
                    value={currentLetter}
                    onChange={(e) => onMappingChange(field.key, e.target.value)}
                    displayEmpty
                    renderValue={(v) => (v ? v : <em>—</em>)}
                  >
                    <MenuItem value="">
                      <em>— skip —</em>
                    </MenuItem>
                    {sniff.available_columns.map((letter) => (
                      <MenuItem key={letter} value={letter}>
                        {letter}
                        {headerByCol[letter] && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            {truncate(headerByCol[letter], 28)}
                          </Typography>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexWrap: 'wrap',
                  }}
                >
                  {currentLetter ? (
                    sample.length > 0 ? (
                      sample.slice(0, 3).map((v, i) => (
                        <Chip
                          key={i}
                          size="small"
                          label={truncate(String(v), 24)}
                          variant="outlined"
                          sx={{ fontFamily: 'monospace', fontSize: 11 }}
                        />
                      ))
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        (no sample values)
                      </Typography>
                    )
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      (skipped — field will be empty)
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}

function buildSampleByCol(sampleRows) {
  const out = {};
  for (const row of sampleRows || []) {
    for (const cell of row) {
      if (!out[cell.col]) out[cell.col] = [];
      out[cell.col].push(cell.value);
    }
  }
  return out;
}

function buildHeaderByCol(headerCells) {
  const out = {};
  for (const cell of headerCells || []) {
    out[cell.col] = String(cell.value);
  }
  return out;
}

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
